import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，找不到该漫画，可能已被下架或链接无效。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.subCategory === comic.subCategory))
    .slice(0, 4);

  const formattedViews = comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toString();

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01',
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.categoryName}漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />
        </div>

        <section className="py-6 md:py-10 px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 w-full sm:w-[280px] lg:w-[320px]">
              <div className="rounded-lg overflow-hidden aspect-[3/4]">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                  title={`${comic.title} - 韩国漫画网`}
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                {comic.title}
              </h1>

              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-foreground-500">作者：</span>
                  <span className="text-sm text-foreground-800 font-medium">{comic.author}</span>
                </div>
                <div className="w-px h-4 bg-background-300"></div>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-foreground-500">评分：</span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-accent-500">
                    <i className="ri-star-fill text-xs"></i>
                    {comic.rating}
                  </span>
                </div>
                <div className="w-px h-4 bg-background-300"></div>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-foreground-500">阅读：</span>
                  <span className="text-sm text-foreground-800">{formattedViews}</span>
                </div>
                <div className="w-px h-4 bg-background-300"></div>
                <div className="flex items-center gap-1.5">
                  <span className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-md ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-5 flex-wrap">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block px-3 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-md whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 text-sm rounded-md whitespace-nowrap">
                  <i className="ri-book-open-line"></i> 共 {comic.chapters} 话
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-100 text-foreground-600 text-sm rounded-md whitespace-nowrap">
                  <i className="ri-time-line"></i> 更新于 {comic.updatedAt}
                </span>
              </div>

              <div className="bg-background-100 rounded-lg p-5 mb-6">
                <h2 className="text-sm font-heading font-semibold text-foreground-900 mb-3">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-line"></i>
                  开始阅读
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 text-sm rounded-full hover:bg-secondary-100 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 text-sm rounded-full hover:bg-secondary-100 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-6">章节目录</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {Array.from({ length: Math.min(comic.chapters, 50) }, (_, i) => (
                <button
                  key={comic.chapters - i}
                  className="flex items-center gap-2 px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all text-left cursor-pointer"
                >
                  <span className="text-xs text-foreground-500 whitespace-nowrap">
                    第{comic.chapters - i}话
                  </span>
                  {i === 0 && (
                    <span className="inline-block px-1.5 py-0.5 bg-accent-100 text-accent-700 text-[10px] rounded whitespace-nowrap">
                      新
                    </span>
                  )}
                </button>
              ))}
            </div>
            {comic.chapters > 50 && (
              <button className="mt-6 mx-auto block px-6 py-2.5 bg-background-100 text-foreground-600 text-sm rounded-full hover:bg-secondary-100 transition-colors whitespace-nowrap cursor-pointer">
                查看全部 {comic.chapters} 话
              </button>
            )}
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-14">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}